package app.footyguide.domain.model

import kotlinx.serialization.Serializable

/**
 * Status de transmissão de um jogo.
 */
@Serializable
enum class BroadcastStatus {
    CONFIRMED,
    PENDING,
    UNAVAILABLE,
}

/**
 * Status da partida na API externa.
 */
@Serializable
enum class MatchStatus {
    /** Não iniciado */
    NOT_STARTED,
    /** Primeiro tempo */
    FIRST_HALF,
    /** Intervalo */
    HALFTIME,
    /** Segundo tempo */
    SECOND_HALF,
    /** Encerrado */
    FINISHED,
    /** Prorrogação */
    EXTRA_TIME,
    /** Pênaltis */
    PENALTIES,
    /** Adiado */
    POSTPONED,
    /** Cancelado */
    CANCELLED,
    /** Desconhecido */
    UNKNOWN,
}

/**
 * Modelo principal de um jogo de futebol.
 *
 * Estrutura global: um jogo é universal; a transmissão varia por país
 * via [channelsByCountry] e [broadcastStatusByCountry].
 *
 * Firestore path: /games/{date}/matches/{matchId} (collection group "matches")
 * Queries suportadas:
 *   - por data:        where("date", ==, "2026-06-14")
 *   - por competição:  where("competitionId", ==, "worldcup")
 *   - por clube:       where("teamIds", array-contains, "flamengo")
 */
@Serializable
data class Game(
    val id: String,
    val competitionId: String,
    /** Rodada da competição, quando aplicável. */
    val matchday: Int? = null,
    /** Fase da competição (ex: REGULAR_SEASON, GROUP_STAGE, QUARTER_FINALS). */
    val stage: String? = null,
    /** Grupo da competição (ex: GROUP_A); nulo fora de fases de grupo. */
    val group: String? = null,
    val homeTeamId: String,
    val awayTeamId: String,
    /** Lista de IDs dos dois times — facilita query array-contains por clube. */
    val teamIds: List<String> = emptyList(),
    val kickoff: String,
    /** Data no formato YYYY-MM-DD — facilita query por dia sem parsear kickoff. */
    val date: String = "",
    /**
     * Canais de transmissão por país.
     * Chave: código ISO do país ("BR", "AR", "US", "WORLD").
     * "WORLD" é usado para competições sem restrição regional (ex: YouTube aberto).
     */
    val channelsByCountry: Map<String, List<String>> = emptyMap(),
    /**
     * Status de broadcast por país.
     * Chave: mesmo código ISO de [channelsByCountry].
     */
    val broadcastStatusByCountry: Map<String, BroadcastStatus> = emptyMap(),
    /** ID do fixture na API externa — usado para buscar escalações. */
    val fixtureApiId: Long? = null,
    val matchStatus: MatchStatus = MatchStatus.NOT_STARTED,
    /** Lista simples de nomes dos titulares do mandante. */
    val homeLineup: List<String> = emptyList(),
    /** Lista simples de nomes dos titulares do visitante. */
    val awayLineup: List<String> = emptyList(),
    /** Formação tática do mandante (ex: "4-3-3"). */
    val homeFormation: String? = null,
    /** Formação tática do visitante. */
    val awayFormation: String? = null,
    /** Titulares do mandante com posição no campo. */
    val homePlayers: List<LineupPlayer> = emptyList(),
    /** Titulares do visitante com posição no campo. */
    val awayPlayers: List<LineupPlayer> = emptyList(),
    val homeScore: Int? = null,
    val awayScore: Int? = null,
    val stadium: String? = null,
    val stadiumCity: String? = null,
    val stadiumCapacity: Int? = null,
    val stadiumImage: String? = null,
    val lastUpdate: String? = null,
) {
    /** Canais para um país específico, com fallback para "WORLD". */
    fun channelsFor(country: String): List<String> =
        channelsByCountry[country] ?: channelsByCountry["WORLD"] ?: emptyList()

    /** Status de broadcast para um país, com fallback para WORLD e depois PENDING. */
    fun broadcastStatusFor(country: String): BroadcastStatus =
        broadcastStatusByCountry[country]
            ?: broadcastStatusByCountry["WORLD"]
            ?: BroadcastStatus.PENDING
}

val Game.isLive: Boolean get() = matchStatus in listOf(
    MatchStatus.FIRST_HALF, MatchStatus.HALFTIME,
    MatchStatus.SECOND_HALF, MatchStatus.EXTRA_TIME,
    MatchStatus.PENALTIES,
)
